/**
 * FCM Service — Web Push Notification Token Management
 * 
 * Handles:
 * - Requesting notification permission from browser
 * - Retrieving FCM token from Firebase
 * - Registering/refreshing token with the chat server (same pattern as mobile app)
 * - Deregistering token on logout
 * 
 * device_id convention: 1 = iOS, 2 = Android, 3 = Web
 */

import { getToken, deleteToken } from 'firebase/messaging';
import { getFirebaseMessaging } from '../config/firebase';
import { store } from '../application/store';
import axios from 'axios';

const WEB_DEVICE_ID = 3; // 1=iOS, 2=Android, 3=Web
const FCM_TOKEN_KEY = 'proton_web_fcm_token';
const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL || 'http://68.178.162.134:5001/api/v1';
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

/**
 * Request browser notification permission.
 * Returns 'granted', 'denied', or 'default'.
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications.');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    console.log(`Notification permission: ${permission}`);
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

/**
 * Get the current FCM token from Firebase Messaging.
 * Requires notification permission to be granted first.
 * 
 * @param {boolean} forceRefresh - If true, deletes old token and gets a new one
 * @returns {string|null} The FCM token or null if unavailable
 */
export const getFcmToken = async (forceRefresh = false) => {
  try {
    const messaging = await getFirebaseMessaging();
    if (!messaging) return null;

    if (!VAPID_KEY) {
      console.error('REACT_APP_FIREBASE_VAPID_KEY is not set in .env. FCM will not work.');
      return null;
    }

    // If force refresh, delete old token first
    if (forceRefresh) {
      try {
        await deleteToken(messaging);
        localStorage.removeItem(FCM_TOKEN_KEY);
      } catch (e) {
        // Token might not exist yet, that's fine
      }
    }

    // Check cached token first (avoid unnecessary Firebase calls)
    if (!forceRefresh) {
      const cachedToken = localStorage.getItem(FCM_TOKEN_KEY);
      if (cachedToken) return cachedToken;
    }

    // Explicitly register the service worker before passing it to Firebase.
    // This avoids the default Firebase registration which can fail silently
    // when the server's SPA catch-all returns index.html instead of the JS file.
    const swRegistration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
      { scope: '/firebase-cloud-messaging-push-scope' }
    );

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });

    if (token) {
      localStorage.setItem(FCM_TOKEN_KEY, token);
      console.log('FCM token obtained successfully');
      return token;
    }

    console.warn('No FCM token returned. Notification permission may not be granted.');
    return null;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

/**
 * Store the FCM token on the chat server.
 * Mirrors the mobile app pattern: POST /user/fcm-token with { token, device_id }
 * 
 * @param {string} fcmToken - The FCM registration token
 * @param {string} chatToken - The chat server JWT token
 */
const storeFcmTokenOnServer = async (fcmToken, chatToken) => {
  try {
    const response = await axios.post(
      `${CHAT_API_URL}/user/fcm-token`,
      {
        token: fcmToken,
        device_id: WEB_DEVICE_ID,
      },
      {
        headers: {
          Authorization: `Bearer ${chatToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    console.log('FCM token registered on server:', response.data?.success ? 'success' : 'failed');
    return response.data;
  } catch (error) {
    console.error('Failed to store FCM token on server:', error?.message || error);
    throw error;
  }
};

/**
 * Register FCM token after login.
 * 1. Requests notification permission
 * 2. Gets FCM token from Firebase
 * 3. Sends token to chat server (same endpoint as mobile app)
 * 
 * This mirrors `registerFcmTokenIfNeeded()` from the mobile app (src/utils/fcmUtils.js)
 */
export const registerFcmTokenIfNeeded = async () => {
  try {
    const state = store.getState();
    const chatToken = state.auth?.chatToken;

    if (!chatToken) {
      console.warn('No chat token available. Skipping FCM token registration.');
      return;
    }

    // Request permission
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.info('Notification permission not granted (%s). Skipping FCM registration.', permission);
      return;
    }

    // Get FCM token
    const fcmToken = await getFcmToken();
    if (!fcmToken) {
      console.warn('Could not obtain FCM token.');
      return;
    }

    // Register with chat server
    await storeFcmTokenOnServer(fcmToken, chatToken);
  } catch (error) {
    console.error('FCM token registration failed:', error);
  }
};

/**
 * Refresh FCM token (e.g., on page visibility change or periodic refresh).
 * Mirrors `refreshFcmTokenIfNeeded()` from the mobile app.
 */
export const refreshFcmTokenIfNeeded = async () => {
  try {
    const state = store.getState();
    const chatToken = state.auth?.chatToken;
    if (!chatToken) return;

    // Force refresh to get a new token
    const freshToken = await getFcmToken(true);
    if (freshToken) {
      await storeFcmTokenOnServer(freshToken, chatToken);
      console.log('FCM token refreshed and updated on server');
    }
  } catch (error) {
    console.error('Error refreshing FCM token:', error);
  }
};

/**
 * Deregister FCM token on logout.
 * Deletes the token from Firebase and clears local storage.
 * 
 * Note: The chat server will eventually deactivate stale tokens via 
 * OptimizedNotificationService.validateTokenBatch()
 */
export const deregisterFcmToken = async () => {
  try {
    const messaging = await getFirebaseMessaging();
    if (messaging) {
      await deleteToken(messaging);
    }
    localStorage.removeItem(FCM_TOKEN_KEY);
    console.log('FCM token deregistered');
  } catch (error) {
    console.error('Error deregistering FCM token:', error);
  }
};
